import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    PLATFORM_ID,
    signal,
    ViewChild,
} from '@angular/core';
import { COMMUNITY_MENU_ITEM, MAIN_MENU_ITEMS, MenuType, STATUS_TOPIC } from '../../../constants/menu.constant';
import { filter, firstValueFrom, forkJoin, skip, Subject, takeUntil } from 'rxjs';
import { ErrorService } from '../../../services/error.service';
import { RecursiveLinkInterface, RecursiveLinkParamInterface, RecursiveLinksModel } from '../menu.model';
import { ProgramFilterInterface } from '../../../models/filter.model';
import { ProgramFilterService } from '../../webinar-course/program-filter.service';
import { AccountService } from '../../../../features/authentication/services/account.service';
import { UserDetailsInterface } from '../../../models/user.model';
import { NavigationEnd, Router } from '@angular/router';
import { StorageService } from '../../../services/storage.service';
import { Language } from '../../../constants/ui-utils.constant';
import { DealsService } from '../../../services/api/deals.service';
import { FaqService } from '../../../services/api/faq.service';
import { isPlatformBrowser } from '@angular/common';
import { CustomToastrService } from '../../../services/custom-toastr.service';
import { MultilingualService } from '../../../services/api/multilingual.service';
import { SystemSettingService } from '../../../services/api/system-setting.service';

@Component({
    selector: 'app-navbar',
    templateUrl: './navbar.component.html',
    styleUrl: './navbar.component.scss',
})
export class NavbarComponent implements OnInit, OnDestroy {
    @ViewChild('dropdownMenu') dropdownMenu!: ElementRef;
    @Input() isMobile = false;
    @Output() closeMobileMenu = new EventEmitter();

    protected isLoading = false;
    protected isClientSide = signal(false);
    protected userDetails: UserDetailsInterface | null = null;
    protected menu: RecursiveLinkInterface[] = MAIN_MENU_ITEMS;
    protected selectedMainMenuType!: MenuType | null;

    private destroy$ = new Subject<void>();

    protected MenuType = MenuType;

    constructor(
        private accountService: AccountService,
        private errorService: ErrorService,
        private programFilterService: ProgramFilterService,
        private router: Router,
        private storageService: StorageService,
        private dealsService: DealsService,
        private faqService: FaqService,
        private customToastrService: CustomToastrService,
        private multilingualService: MultilingualService,
        private systemSettingService: SystemSettingService,
        @Inject(PLATFORM_ID) private platformId: object
    ) {
        // Consolidated client-side initialization.
        // Developer Note: We cannot utilize "afterNextRender"
        // for this segment as the API calls triggered after the first
        // render would invoke NG0100 error.
        // For more info, refer to this link:
        // https://angular.dev/errors/NG0100
        this.isClientSide.set(isPlatformBrowser(this.platformId));
    }

    async ngOnInit(): Promise<void> {
        if (!this.isClientSide())
            return;

        // Developer Note: Moved away from using "afterNextRender" after 
        // previous encounter with [NG0100: ExpressionChangedAfterItHasBeenCheckedError].
        this.isLoading = true;

        try {
            await this.getUserDetails();
            if (!this.userDetails) {
                this.isLoading = false;
                return;
            }

            // Trigger fetchTopics if not already loaded (e.g., when invalid-url page bypasses admin layout)
            this.programFilterService.fetchTopics();

            await this.buildMenu();
            this.closeMenuOnRouteChange();

            // TPOD-2263: rebuild the menu reactively on language change so topic
            // submenu labels (API content, not i18n pipe strings) stay in sync
            // without the old full-page reload.
            this.subscribeToLanguageChange();
        }
        catch (error) {
            this.errorService.showError(error);
        }
        finally {
            this.isLoading = false;
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private async buildMenu(): Promise<void> {
        // Reset to a fresh copy of the static base first: the topic decoration
        // attaches children and the deals/FAQ checks remove items, so re-running
        // the build against an already-built menu would compound. MAIN_MENU_ITEMS
        // is plain data, so structuredClone is safe.
        this.menu = structuredClone(MAIN_MENU_ITEMS);

        await this.getTopicsFilter();
        await this.checkDealsAvailability();
        await this.checkFaqAvailability();
        await this.checkCommunityAvailability();
    }

    // TPOD-3318: the menu is now a tree (Tax Topics / Webinars own the nodes that
    // used to be top-level), so decoration has to search rather than scan one array.
    // buildMenu() structuredClones MAIN_MENU_ITEMS first, so mutating a returned
    // node is safe — it is never the shared constant.
    private findMenuItem(
        menuType: MenuType,
        items: RecursiveLinkInterface[] = this.menu,
    ): RecursiveLinkInterface | undefined {
        for (const item of items) {
            if (item.menuType === menuType) {
                return item;
            }

            const found = item.children && this.findMenuItem(menuType, item.children);
            if (found) {
                return found;
            }
        }

        return undefined;
    }

    private removeMenuItem(menuType: MenuType, items: RecursiveLinkInterface[] = this.menu): void {
        const index = items.findIndex((item) => item.menuType === menuType);
        if (index !== -1) {
            items.splice(index, 1);
            return;
        }

        items.forEach((item) => item.children && this.removeMenuItem(menuType, item.children));
    }

    private subscribeToLanguageChange(): void {
        // currentLanguage is a BehaviorSubject — skip its current value (the menu
        // was just built in that language) and only rebuild on actual changes.
        this.multilingualService.currentLanguage.pipe(skip(1), takeUntil(this.destroy$)).subscribe(() => {
            this.buildMenu().catch((error) => this.errorService.showError(error));
        });
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (this.dropdownMenu) {
            if (!this.dropdownMenu.nativeElement.contains(event.target)) {
                this.selectedMainMenuType = null;
            }
        }
    }

    protected onLinkClick(param?: RecursiveLinkParamInterface): void {
        if (param?.topic || Number.isInteger(param?.programType) || Number.isInteger(param?.webinarType)) {
            this.programFilterService.filterNavbarDataSubject.next(param!);
        }

        this.selectedMainMenuType = null;
        this.closeMobileMenu.emit();
    }

    protected toggleMenu(menuType?: MenuType, toggle = false): void {
        if (!toggle) {
            this.selectedMainMenuType = null;

            return;
        }

        if (menuType !== undefined) {
            this.selectedMainMenuType = menuType;
        }
    }

    private closeMenuOnRouteChange(): void {
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.selectedMainMenuType = null;
            }
        });
    }

    private async getUserDetails(): Promise<void> {
        try {
            const resp = await firstValueFrom(this.accountService.getUserDetails());
            if (resp) {
                this.userDetails = resp;
            }
        }
        catch (error) {
            // Error will be handled by ngOnInit's catch block
            throw error;
        }
    }

    private async getTopicsFilter(): Promise<void> {
        try {
            const response = await firstValueFrom(
                this.programFilterService.topics$
                    .pipe(filter((response) => response && Object.keys(response).length > 0))
            );

            this.assignTopicsToMenu(response, MenuType.UpdateClasses, 'updateClasses', '/program/list/update-classes',);
            this.assignTopicsToMenu(response, MenuType.EInvoicing, 'eInvoicing', '/program/list/e-invoicing');
            this.assignTopicsToMenu(response, MenuType.IncomeTax, 'incomeTax', '/program/list/income-tax');
            this.assignTopicsToMenu(response, MenuType.Forms, 'forms', '/program/list/forms');
            this.assignTopicsToMenu(response, MenuType.SST, 'sst', '/program/list/sst');
        }
        catch (error) {
            // Error will be handled by ngOnInit's catch block
            throw error;
        }
    }

    private assignTopicsToMenu(
        responseData: ProgramFilterInterface,
        menuType: MenuType,
        statusTopicKey: keyof typeof STATUS_TOPIC,
        baseLink: string,
    ): void {
        const topicData = responseData.programTopics
            .filter((topic) => topic.name.toLowerCase() === STATUS_TOPIC[statusTopicKey].toLowerCase())
            .map((topic) => new RecursiveLinksModel(topic, this.storageService.language));

        if (!topicData.length) {
            return;
        }

        const target = this.findMenuItem(menuType);
        if (!target) {
            return;
        }

        target.link = baseLink;
        target.param = { topic: topicData[0].id };

        const children = topicData[0].children;
        if (children?.length) {
            target.children = this.filterChildrenData(children).map((child: RecursiveLinksModel) => ({
                ...child,
                link: baseLink,
            }));
        }
    }

    private filterChildrenData(children?: RecursiveLinksModel[]): any {

        let byPassCategory: string[] = [];

        let tempChildren = children;
        
        let tempFilter = tempChildren?.filter(f => {
            let tempTranslation = '';

            let languageFilter = f.model.translations.filter(t => {
                return t.language == Language.EN;
            })

            tempTranslation = (languageFilter[0] && languageFilter[0].description) ? languageFilter[0].description.toLowerCase() : f.label.toLowerCase();

            return !byPassCategory.includes(tempTranslation);
        })

        return tempFilter;
    }

    private async checkDealsAvailability(): Promise<void> {
        try {
            const response = await firstValueFrom(this.dealsService.getDealsCount());
            if (response.result.total === 0) {
                // Deals moved under Resources in TPOD-3318 — remove by tree walk.
                this.removeMenuItem(MenuType.Deals);
            }
        }
        catch {
            // On error, hide the deals menu for safety.
            this.removeMenuItem(MenuType.Deals);
            this.customToastrService.showError('Unable to load deals information. Please try again later.');
        }
    }

    /**
     * The Community entry is shown only to members the backend flag lets in (`isCommunityFeatureEnabled`, per member under allow-list
     * mode). It is not in the base menu and is added here, last, so every failure before this point leaves it out; the service fails
     * closed too, and the API enforces the same rule on every call regardless. Its place is right after Ask an Expert.
     */
    private async checkCommunityAvailability(): Promise<void> {
        const config = await firstValueFrom(this.systemSettingService.getConfig());
        if (!config.isCommunityFeatureEnabled) return;
        // buildMenu() can overlap itself (a language change while the first build still awaits the network), and both runs share `menu`.
        if (this.menu.some((menu) => menu.menuType === MenuType.Community)) return;

        const afterExpert = this.menu.findIndex((menu) => menu.menuType === MenuType.AskAnExpert) + 1;
        const at = afterExpert > 0 ? afterExpert : this.menu.length;
        this.menu = [...this.menu.slice(0, at), structuredClone(COMMUNITY_MENU_ITEM), ...this.menu.slice(at)];
    }

    private async checkFaqAvailability(): Promise<void> {
        try {
            const response = await firstValueFrom(this.faqService.getFaqCount());
            if (response.total === 0) {
                this.removeMenuItem(MenuType.FAQ);
            }
        }
        catch {
            // On error, hide the FAQ sub-menu for safety.
            this.removeMenuItem(MenuType.FAQ);
            this.customToastrService.showError('Unable to load FAQ information. Please try again later.');
        }
    }
}
